import Mapbox from '../Mapbox';

/**
 * SalesMapSearch — Alpine.js component for the sales results page with map.
 *
 * Simplified version of BuildingMapSearch.js, using Eloquent geo endpoints
 * instead of Meilisearch. Supports both new_project (SaleBuilding) and
 * resale (Unit for_sale) listings on the same map.
 */
export default function salesMapSearch(
    initialCenter = [-70.6483, -33.4569],
    initialZoom = 12,
    options = {},
    communeSlug = '',
    communeId = null
) {
    return {
        runWithOptionalViewTransition(callback) {
            if (!document.startViewTransition) {
                callback();
                return null;
            }

            const transition = document.startViewTransition(callback);
            const swallowSkippedTransition = (error) => {
                if (error?.name === 'AbortError' || error?.message === 'Transition was skipped') {
                    return;
                }

                console.error('Sales view transition error:', error);
            };

            ['ready', 'updateCallbackDone', 'finished'].forEach((key) => {
                const promise = transition?.[key];
                if (promise?.catch) {
                    promise.catch(swallowSkippedTransition);
                }
            });

            return transition;
        },

        // ─── Map ───
        mapbox: null,
        isMapReady: false,

        // ─── Results ───
        buildings: [],
        totalBuildings: 0,
        loading: false,
        hasSearched: false,
        error: null,

        // ─── Sort ───
        sortBy: null,
        sortOptions: [
            { value: 'relevance', label: 'Recomendados' },
            { value: 'most-profitable', label: 'Más rentable' },
            { value: 'price-asc', label: 'Menor precio' },
            { value: 'price-desc', label: 'Mayor precio' },
        ],


        setCommuneSearchContext({ slug = null, communeId = null, name = '' } = {}) {
            this.communeSlug = slug || null;
            this.communeId = communeId || null;
            this.communeName = name || '';
            this.isCommuneSearch = Boolean(this.communeSlug || this.communeId);
        },

        syncUrlForMapTarget({ center, zoom } = {}) {
            this.updateUrl(false, false, center, zoom);
        },

        commitBedroomTempFilters() {
            this.filters.bedrooms = [...(this.tempFilters.bedrooms || [])];
            this.filters.bedrooms_exact_match = !!this.tempFilters.bedrooms_exact_match;
        },

        hasPendingBedroomTempChanges() {
            const temp = this.tempFilters.bedrooms || [];
            const committed = this.filters.bedrooms || [];
            if (temp.length !== committed.length) return true;
            for (let i = 0; i < temp.length; i++) {
                if (temp[i] !== committed[i]) return true;
            }
            return this.filters.bedrooms_exact_match !== !!this.tempFilters.bedrooms_exact_match;
        },

        // ─── Filters ───
        filters: {
            bedrooms: [],
            bedrooms_exact_match: false,
            bathrooms: [],
            min_price: null,
            max_price: null,
            has_parking: false,
            has_store: false,
            type: null, // 'new_project', 'resale', or null (both)
            // Sale-specific filters
            guaranteed_rent: false,
            installment_downpayment: false,
            bono_pie: false,
            immediate_delivery: false,
            future_delivery: false,
        },

        // Filtros temporales (para el drawer)
        tempFilters: {
            bedrooms: [],
            bedrooms_exact_match: false,
            bathrooms: [],
            min_price: null,
            max_price: null,
            has_parking: false,
            has_store: false,
            type: null,
            guaranteed_rent: false,
            installment_downpayment: false,
            bono_pie: false,
            immediate_delivery: false,
            future_delivery: false,
        },

        // ─── Commune ───
        communeSlug: communeSlug || '',
        communeId: communeId || null,
        communeName: '',
        isCommuneSearch: !!(communeSlug || communeId),
        defaultCenter: {
            lng: initialCenter[0],
            lat: initialCenter[1],
        },
        defaultZoom: initialZoom,

        // ─── Map interaction ───
        showSearchButton: false,
        lastProgrammaticMapMove: 0,
        viewMode: 'list',

        // ─── Config ───
        config: {
            limit: options.limit || 2000,
            autoSearch: options.autoSearch ?? true,
            debounceTime: options.debounceTime || 500,
            primaryColor: options.primaryColor || '#3B82F6',
            markerColor: options.markerColor || '#3B82F6',
            debug: options.debug || false,
        },

        // ─── Internal ───
        currentSearchId: 0,
        searchTimeout: null,
        abortController: null,

        // ────────────────────────────────────────────────────
        // Init
        // ────────────────────────────────────────────────────
        init() {
            if (this.mapbox) return;

            // Sync commune name from URL
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('q')) {
                this.communeName = urlParams.get('q');
            } else if (this.communeSlug) {
                this.communeName = this.communeSlug
                    .replace(/-/g, ' ')
                    .replace(/\b\w/g, c => c.toUpperCase());
            }

            // Load map config from URL
            const mapConfig = this.loadFromUrl();
            const center = mapConfig?.center || initialCenter;
            const zoom = mapConfig?.zoom || initialZoom;

            const apiKey = import.meta.env.VITE_RADAR_PUBLISHABLE_KEY || import.meta.env.VITE_MAPTILER_TOKEN;

            this.initializeMap(apiKey, center, zoom);
            this.setupEventListeners();

            this.lastProgrammaticMapMove = Date.now();
            this.updateUrl();
        },

        // ────────────────────────────────────────────────────
        // Map setup
        // ────────────────────────────────────────────────────
        initializeMap(apiKey, center, zoom) {
            const mapEl = document.getElementById('map');
            if (!mapEl || mapEl.classList.contains('maplibregl-map')) return;

            this.mapbox = new Mapbox(apiKey, {
                debug: this.config.debug,
                primaryColor: this.config.primaryColor,
                markerColor: this.config.markerColor,
                textColor: this.config.primaryColor,
            });

            this.overrideVentasMapClickHandlers();

            this.mapbox.makeMap('map', { center, zoom, minZoom: 4, maxZoom: 17 }, {
                showCompass: false,
                showZoom: true,
            });
        },

        overrideVentasMapClickHandlers() {
            if (!this.mapbox) return;

            const component = this;
            const mapbox = this.mapbox;

            mapbox.handleClusterClick = function (e) {
                if (e) e._featureClickHandled = true;

                component.runWithOptionalViewTransition(() => {
                    mapbox.dispatchCustomEvent('toogleOpenClusters', { show: true });
                });

                const features = mapbox.map.queryRenderedFeatures(e.point, {
                    layers: ['clusters'],
                });

                if (!features || features.length === 0) {
                    mapbox.warn('No cluster features found at click point');
                    return;
                }

                const clusterId = features[0].properties.cluster_id;
                const pointCount = features[0].properties.point_count;

                mapbox.log('Cluster clicked', { clusterId, point_count: pointCount });

                mapbox.highlightLayer('clusters', 'cluster_id', clusterId);
                mapbox.resetLayerStyle('unclustered');

                const clusterSource = mapbox.map.getSource('buildings');

                if (!clusterSource) {
                    console.error('❌ No cluster source found!');
                    return;
                }

                clusterSource.getClusterLeaves(clusterId, pointCount, 0)
                    .then((buildings) => {
                        if (!buildings || buildings.length === 0) {
                            mapbox.warn('No buildings found in cluster');
                            return;
                        }

                        const filterBuildings = buildings.map(
                            building => building.properties.building_id
                        );

                        mapbox.log('Cluster buildings', filterBuildings);

                        component.runWithOptionalViewTransition(() => {
                            mapbox.dispatchCustomEvent('cluster', {
                                ids: filterBuildings,
                            });
                        });
                    })
                    .catch((err) => {
                        console.error('❌ Error getting cluster leaves:', err);
                    });
            };

            mapbox.handleUnclusteredClick = function (e) {
                if (e) e._featureClickHandled = true;

                component.runWithOptionalViewTransition(() => {
                    mapbox.dispatchCustomEvent('toogleOpenClusters', { show: true });
                });

                const features = mapbox.map.queryRenderedFeatures(e.point, {
                    layers: ['unclustered'],
                });

                if (!features || features.length === 0) {
                    mapbox.warn('No unclustered features found at click point');
                    return;
                }

                const buildingId = features[0].properties.building_id;

                mapbox.log('Unclustered point clicked', { buildingId });

                mapbox.highlightLayer('unclustered', 'building_id', buildingId);
                mapbox.resetLayerStyle('clusters');

                component.runWithOptionalViewTransition(() => {
                    mapbox.dispatchCustomEvent('unclustered', {
                        ids: [buildingId],
                    });
                });

                const element = document.getElementById('cluster_section');
                if (element) {
                    component.runWithOptionalViewTransition(() => {
                        element.classList.remove('hidden');
                        element.classList.add('block');
                    });
                }
            };
        },

        setupEventListeners() {
            // Track whether initial search has fired
            let initialSearchDone = false;

            const doInitialSearch = () => {
                if (initialSearchDone) return;
                initialSearchDone = true;

                if (this.communeId || this.communeSlug) {
                    const urlParams = new URLSearchParams(window.location.search);
                    const lat = parseFloat(urlParams.get('lat'));
                    const lng = parseFloat(urlParams.get('lng'));
                    const zoom = parseFloat(urlParams.get('zoom')) || 13;
                    this.performCommuneSearch({ lat, lng, zoom });
                } else {
                    this.searchBuildings('initial_load');
                }
            };

            // Map loaded
            window.addEventListener('mapbox-load', () => {
                this.isMapReady = true;
                if (this.mapbox?.map) this.mapbox.map.resize();

                setTimeout(doInitialSearch, 800);
            }, { once: true });

            // Fallback: if map tiles fail to load (e.g. API key domain restriction),
            // still trigger the initial data search after a timeout
            setTimeout(() => {
                if (!initialSearchDone) {
                    doInitialSearch();
                }
            }, 4000);

            // Map move end
            window.addEventListener('mapbox-moveend', (e) => {
                const isProgrammatic = e.detail?.programmatic || false;
                const source = e.detail?.source || '';
                const now = Date.now();
                const timeSinceProgrammatic = now - this.lastProgrammaticMapMove;
                const isManual = !isProgrammatic && timeSinceProgrammatic > 3500;

                if (isManual) {
                    this.showSearchButton = true;
                }

                this.updateUrl(true, isProgrammatic);

                if (source === 'autocomplete' || e.detail?.forceSearch) {
                    this.searchBuildings(source || 'forced_map_move');
                } else if (isProgrammatic) {
                    // skip
                } else if (timeSinceProgrammatic < 5000) {
                    // skip grace period
                } else if (this.config.autoSearch) {
                    this.debouncedSearch();
                }
            });

            // Cluster click → match buildings and dispatch Alpine event
            window.addEventListener('cluster', (e) => {
                if (!e.detail?.ids) return;
                const ids = e.detail.ids;
                const matched = this.buildings.filter(b => {
                    const bId = b.source_id || b.building_id;
                    return ids.includes(bId) || ids.includes(String(bId));
                });
                if (matched.length > 0) {
                    this.runWithOptionalViewTransition(() => {
                        window.dispatchEvent(new CustomEvent('cluster-selected', {
                            detail: {
                                buildings: matched,
                                buildingIds: ids,
                                count: matched.length,
                            },
                        }));
                    });
                }
            });

            // Single marker click
            window.addEventListener('unclustered', (e) => {
                if (!e.detail?.ids?.[0]) return;
                const id = e.detail.ids[0];
                const found = this.buildings.find(b => {
                    const bId = b.source_id || b.building_id;
                    return bId == id;
                });
                if (found) {
                    this.runWithOptionalViewTransition(() => {
                        window.dispatchEvent(new CustomEvent('building-selected', {
                            detail: {
                                building: found,
                                buildingId: found.source_id,
                            },
                        }));
                    });
                }
            });

            // Autocomplete selected
            window.addEventListener('autocomplete-selected', (e) => {
                const detail = e.detail;
                if (detail.slug || detail.communeId) {
                    this.setCommuneSearchContext({
                        slug: detail.slug,
                        communeId: detail.communeId,
                        name: detail.name,
                    });
                    this.performCommuneSearch(detail.slug || detail.communeId);
                } else if (detail.lat && detail.lng) {
                    this.lastProgrammaticMapMove = Date.now();
                    this.mapbox.map.flyTo({
                        center: [detail.lng, detail.lat],
                        zoom: 15,
                        essential: true
                    });
                    this.setCommuneSearchContext({});
                    setTimeout(() => {
                        this.searchBuildings('autocomplete_submit');
                    }, 1200);
                }
            });

            // Autocomplete submit
            window.addEventListener('autocomplete-submit', (e) => {
                e.preventDefault();
                const detail = e.detail;

                // Aplicar filtros de dormitorios
                if (detail.bedrooms && detail.bedrooms.length > 0) {
                    this.filters.bedrooms = detail.bedrooms;
                    this.filters.bedrooms_exact_match = detail.exactMatch || false;
                }

                // Preservar type
                if (detail.type) {
                    this.filters.type = detail.type;
                }

                if (detail.slug || detail.communeId) {
                    this.setCommuneSearchContext({
                        slug: detail.slug,
                        communeId: detail.communeId,
                        name: detail.search,
                    });

                    if (detail.lat && detail.lng) {
                        this.performCommuneSearch({ lat: detail.lat, lng: detail.lng, zoom: detail.zoom });
                    } else {
                        this.performCommuneSearch(detail.slug || detail.communeId);
                    }
                } else if (detail.lat && detail.lng) {
                    this.setCommuneSearchContext({});
                    const zoom = parseFloat(detail.zoom) || 13;
                    this.lastProgrammaticMapMove = Date.now();
                    this.updateUrl(false, false, { lat: detail.lat, lng: detail.lng }, zoom);
                    if (this.mapbox && this.mapbox.map) {
                        this.mapbox.map.flyTo({
                            center: [detail.lng, detail.lat],
                            zoom: zoom,
                            duration: 1500
                        }, { programmatic: true, source: 'autocomplete' });
                        setTimeout(() => {
                            this.searchBuildings('autocomplete_submit');
                        }, 1600);
                    }
                } else {
                    this.searchBuildings();
                }
            });
        },

        // ────────────────────────────────────────────────────
        // Search methods
        // ────────────────────────────────────────────────────
        debouncedSearch() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.searchBuildings('debounced');
            }, this.config.debounceTime);
        },

        /**
         * Calculate synthetic bounds when the map canvas is collapsed (w-0 in list mode)
         * or for initial load where we want a wide search area.
         */
        calculateSyntheticBounds(center, zoom) {
            // Approximate degrees per zoom level (at equator)
            const degreesPerZoom = 360 / Math.pow(2, zoom);
            const latSpan = degreesPerZoom / 2;
            const lngSpan = degreesPerZoom;
            return {
                north: center.lat + latSpan,
                south: center.lat - latSpan,
                east: center.lng + lngSpan,
                west: center.lng - lngSpan,
            };
        },

        async searchBuildings(reason = 'unknown') {
            if (!this.mapbox?.map) {
                return;
            }

            this.openMobileResultsSheetOnNextResults = this.viewMode === 'map'
                && this.isMobileViewport
                && reason === 'manual_search_area';

            // Hide search button for certain reasons
            if (['manual_search_area', 'autocomplete_submit', 'initial_load'].includes(reason)) {
                this.showSearchButton = false;
            }

            // Get bounds — use synthetic bounds when the map isn't reliably visible.
            // In list mode the map container is collapsed (w-0), so real getBounds()
            // may return stale/wrong values. Use wide synthetic bounds instead.
            let boundsArray;
            const canvas = this.mapbox.map.getCanvas();
            const isCollapsed = !canvas || canvas.clientWidth < 50 || canvas.clientHeight < 50;
            const needsSyntheticBounds = reason === 'initial_load' || isCollapsed
                || (this.viewMode === 'list');

            if (needsSyntheticBounds) {
                const center = this.mapbox.map.getCenter();
                const zoom = this.mapbox.map.getZoom();
                boundsArray = this.calculateSyntheticBounds(
                    { lat: center.lat, lng: center.lng },
                    zoom
                );
            } else {
                const bounds = this.mapbox.map.getBounds();
                boundsArray = {
                    north: bounds.getNorth(),
                    south: bounds.getSouth(),
                    east: bounds.getEast(),
                    west: bounds.getWest(),
                };
            }

            // Guard against rapid programmatic moves
            if (!['manual_search_area', 'autocomplete_submit', 'initial_load', 'filter_change', 'sort_change'].includes(reason)) {
                if (Date.now() - this.lastProgrammaticMapMove < 5000) return;
            }

            // Cancel previous request
            if (this.abortController) this.abortController.abort();
            this.abortController = new AbortController();

            const searchId = ++this.currentSearchId;
            this.loading = true;
            this.error = null;

            try {
                const payload = {
                    bounds: boundsArray,
                    filters: this.getActiveFilters(),
                    limit: this.config.limit,
                };
                if (this.sortBy) {
                    payload.sort = this.sortBy;
                }
                const response = await fetch('/api/sales/search-by-bbox', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                    },
                    body: JSON.stringify(payload),
                    signal: this.abortController.signal,
                });

                const data = await response.json();

                if (searchId !== this.currentSearchId) {
                    return;
                }

                if (data.success) {
                    const buildings = data.data?.buildings || [];
                    this.buildings = buildings;
                    this.totalBuildings = data.data?.total || buildings.length;
                    this.updateMapMarkers(buildings);

                    if (buildings.length > 0) {
                        // Do NOT fit on filter_change — user has a pinned position
                        if (reason !== 'filter_change') {
                            this.lastProgrammaticMapMove = Date.now();
                            this.fitMapToBuildingBounds(buildings);
                        }
                    }
                }
            } catch (e) {
                if (e.name === 'AbortError') {
                    return;
                }
                this.error = 'Error al buscar propiedades';
            } finally {
                if (searchId === this.currentSearchId) {
                    this.loading = false;
                    this.hasSearched = true;
                }
            }
        },

        async performCommuneSearch(coords = {}, reason = 'initial_load') {
            if (!this.communeId && !this.communeSlug) return;

            // Cancel any in-flight bbox search
            if (this.abortController) this.abortController.abort();
            this.abortController = new AbortController();

            const searchId = ++this.currentSearchId;
            this.loading = true;
            this.error = null;

            try {
                const communeIdInt = this.communeId ? parseInt(this.communeId) : null;
                if (!communeIdInt) {
                    this.error = 'Comuna no encontrada';
                    return;
                }

                const payload = {
                    commune_id: communeIdInt,
                    filters: this.getActiveFilters(),
                    limit: this.config.limit,
                };
                if (this.sortBy) {
                    payload.sort = this.sortBy;
                }

                const response = await fetch('/api/sales/search-by-commune', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                    },
                    body: JSON.stringify(payload),
                    signal: this.abortController.signal,
                });

                const data = await response.json();

                if (searchId !== this.currentSearchId) return;

                if (data.success) {
                    const buildings = data.data?.buildings || [];
                    this.buildings = buildings;
                    this.totalBuildings = data.data?.total || buildings.length;

                    if (buildings.length > 0) {
                        // Do NOT fit on filter_change — user has a pinned position
                        if (reason !== 'filter_change') {
                            this.lastProgrammaticMapMove = Date.now();
                            this.fitMapToBuildingBounds(buildings);
                        }
                        this.updateMapMarkers(buildings);
                    } else if (coords.lat && coords.lng) {
                        // Fly to the commune coords even if no results
                        this.lastProgrammaticMapMove = Date.now();
                        this.mapbox.map.flyTo({
                            center: [coords.lng, coords.lat],
                            zoom: coords.zoom || 13,
                            duration: 1500,
                        }, { programmatic: true });
                        this.updateMapMarkers([]);
                    }
                    this.syncUrlForMapTarget();
                }
            } catch (e) {
                if (e.name === 'AbortError') return;
                console.error('Commune search error:', e);
                this.error = 'Error al buscar en la comuna';
                // Fallback to bbox search
                this.searchBuildings('initial_load');
            } finally {
                if (searchId === this.currentSearchId) {
                    this.loading = false;
                    this.hasSearched = true;
                }
            }
        },

        // ────────────────────────────────────────────────────
        // Filters
        // ────────────────────────────────────────────────────
        getActiveFilters() {
            const active = {};

            if (this.filters.bedrooms?.length > 0) {
                active.bedrooms = [...this.filters.bedrooms];
                if (this.filters.bedrooms_exact_match) {
                    active.bedrooms_exact_match = true;
                }
            }
            if (this.filters.bathrooms?.length > 0) active.bathrooms = [...this.filters.bathrooms];
            if (this.filters.min_price) active.min_price = this.filters.min_price;
            if (this.filters.max_price) active.max_price = this.filters.max_price;
            if (this.filters.has_parking) active.has_parking = true;
            if (this.filters.has_store) active.has_store = true;
            if (this.filters.type) active.type = this.filters.type;

            // Sale-specific filters
            if (this.filters.guaranteed_rent) active.guaranteed_rent = true;
            if (this.filters.installment_downpayment) active.installment_downpayment = true;
            if (this.filters.bono_pie) active.bono_pie = true;
            if (this.filters.immediate_delivery) active.immediate_delivery = true;
            if (this.filters.future_delivery) active.future_delivery = true;

            if (this.communeId) active.commune_id = parseInt(this.communeId);

            return active;
        },

        applyFilters() {
            if (this.isCommuneSearch && this.communeId) {
                this.performCommuneSearch({}, 'filter_change');
            } else {
                this.searchBuildings('filter_change');
            }
            this.updateUrl();
        },

        /**
         * Sincronizar filtros principales a temporales
         */
        syncFiltersToTemp() {
            this.tempFilters = JSON.parse(JSON.stringify(this.filters));
        },

        /**
         * Aplicar filtros temporales a principales y buscar
         */
        applyTempFilters() {
            this.filters = JSON.parse(JSON.stringify(this.tempFilters));
            this.applyFilters();

            if (window.assetplanAnalytics) {
                window.assetplanAnalytics.for('sitio_web').track('filters_apply');
            }
        },

        getActiveFiltersCount() {
            let count = 0;

            if (this.filters.bedrooms?.length > 0) count++;
            if (this.filters.bathrooms?.length > 0) count++;
            if (this.filters.min_price || this.filters.max_price) count++;

            if (this.filters.has_parking) count++;
            if (this.filters.has_store) count++;
            if (this.filters.guaranteed_rent) count++;
            if (this.filters.installment_downpayment) count++;
            if (this.filters.bono_pie) count++;
            if (this.filters.immediate_delivery) count++;
            if (this.filters.future_delivery) count++;

            return count;
        },

        clearFilters() {
            const currentType = this.filters.type;
            const currentCommuneSlug = this.communeSlug;
            const currentCommuneId = this.communeId;
            const currentCommuneName = this.communeName;
            const keepCommuneContext = !!(currentCommuneSlug || currentCommuneId);

            this.filters = {
                bedrooms: [],
                bedrooms_exact_match: false,
                bathrooms: [],
                min_price: null,
                max_price: null,
                has_parking: false,
                has_store: false,
                type: currentType,
                guaranteed_rent: false,
                installment_downpayment: false,
                bono_pie: false,
                immediate_delivery: false,
                future_delivery: false,
            };

            if (keepCommuneContext) {
                this.setCommuneSearchContext({
                    slug: currentCommuneSlug,
                    communeId: currentCommuneId,
                    name: currentCommuneName,
                });
            } else {
                this.setCommuneSearchContext({});
            }

            if (! keepCommuneContext && this.mapbox?.map) {
                this.lastProgrammaticMapMove = Date.now();
                this.mapbox.map.jumpTo({
                    center: [this.defaultCenter.lng, this.defaultCenter.lat],
                    zoom: this.defaultZoom,
                });
            }

            window.dispatchEvent(new CustomEvent('clear-autocomplete-search'));
            this.syncFiltersToTemp();
            this.updateUrl(false, false, this.defaultCenter, this.defaultZoom);
            this.applyFilters();
        },

        /**
         * Toggle filtro de dormitorios (Temporal — para el drawer)
         */
        toggleTempBedroom(bedroom) {
            const index = this.tempFilters.bedrooms.indexOf(bedroom);

            if (index > -1) {
                this.tempFilters.bedrooms = [];
            } else {
                this.tempFilters.bedrooms = [bedroom];
            }
        },

        /**
         * Toggle filtro de baños (Temporal — para el drawer)
         */
        toggleTempBathroom(bathroom) {
            const index = this.tempFilters.bathrooms.indexOf(bathroom);

            if (index > -1) {
                this.tempFilters.bathrooms = [];
            } else {
                this.tempFilters.bathrooms = [bathroom];
            }
        },

        /**
         * Solo permite números
         */
        onlyNumbers(event) {
            if (!/\d/.test(event.key)) {
                event.preventDefault();
            }
        },

        /**
         * Gestiona la entrada de datos para filtros de precio.
         */
        handlePriceInput(event, key) {
            const input = event.target;
            // Quitamos los puntos y cualquier cosa no numérica
            const cleanValue = input.value.replaceAll(/\D/g, '');

            if (cleanValue === '') {
                this.tempFilters[key] = null;
                input.value = '';
                return;
            }

            // Guardamos el número real (limpio) en el objeto temporal
            const numericValue = Number.parseInt(cleanValue);

            this.tempFilters[key] = numericValue;

            // Actualizamos el valor visual del input usando la función de formateo
            input.value = this.formatNumber(numericValue);
        },

        /**
         * Formatear número con separadores de miles
         */
        formatNumber(number) {
            return new Intl.NumberFormat('es-CL').format(number);
        },

        /**
         * Obtener label del sort actual
         */
        getCurrentSortLabel() {
            if (!this.sortBy) {
                return 'Ordenar por';
            }
            const option = this.sortOptions.find(o => o.value === this.sortBy);
            return option ? 'Ordenar por: ' + option.label : 'Ordenar por';
        },

        // ────────────────────────────────────────────────────
        // Map markers (GeoJSON)
        // ────────────────────────────────────────────────────
        updateMapMarkers(source) {
            if (!this.mapbox?.map) return;

            if (!source || source.length === 0) {
                this.mapbox.dataToMap({ type: 'FeatureCollection', features: [] });
                return;
            }

            const geojson = this.buildGeoJSON(source);
            this.mapbox.dataToMap(geojson);
        },

        buildGeoJSON(source) {
            const features = [];

            source.forEach(item => {
                const lat = parseFloat(item.latitude);
                const lng = parseFloat(item.longitude);

                if (!lat || !lng || isNaN(lat) || isNaN(lng)) return;
                if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return;

                features.push({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [lng, lat],
                    },
                    properties: {
                        building_id: item.source_id,
                        nombre: item.name || '',
                        direccion: item.address || '',
                        comuna: item.commune || '',
                        min_price: item.sale_price_uf || 0,
                        units: 1,
                        type: item.type, // 'new_project' or 'resale'
                        servicio_pro: false,
                        multifamily: item.type === 'new_project',
                        super_promo: false,
                        has_discount: false,
                        accepts_pets: false,
                    },
                });
            });

            return { type: 'FeatureCollection', features };
        },

        fitMapToBuildingBounds(buildings) {
            if (!this.mapbox?.map || buildings.length === 0) return;

            this.lastProgrammaticMapMove = Date.now();

            const allCoords = buildings
                .filter(b => b.latitude && b.longitude)
                .map(b => ({
                    lat: parseFloat(b.latitude),
                    lng: parseFloat(b.longitude),
                }))
                .filter(c => !isNaN(c.lat) && !isNaN(c.lng));

            if (allCoords.length === 0) return;

            // Use median to find center and filter outliers (same as rental page)
            const sortedLats = [...allCoords].sort((a, b) => a.lat - b.lat);
            const sortedLngs = [...allCoords].sort((a, b) => a.lng - b.lng);
            const mid = Math.floor(allCoords.length / 2);
            const medianLat = sortedLats[mid].lat;
            const medianLng = sortedLngs[mid].lng;

            // Filter outliers (0.5 degrees ≈ 55km)
            const OUTLIER_THRESHOLD = 0.5;
            const coords = allCoords
                .filter(c =>
                    Math.abs(c.lat - medianLat) < OUTLIER_THRESHOLD &&
                    Math.abs(c.lng - medianLng) < OUTLIER_THRESHOLD
                )
                .map(c => [c.lng, c.lat]);

            if (coords.length === 0) {
                this.mapbox.map.flyTo({
                    center: [medianLng, medianLat],
                    zoom: 13,
                    duration: 1500,
                }, { programmatic: true });
                return;
            }

            if (coords.length === 1) {
                this.mapbox.map.flyTo({
                    center: coords[0],
                    zoom: 14,
                    duration: 1500,
                }, { programmatic: true });
                return;
            }

            const lngs = coords.map(c => c[0]);
            const lats = coords.map(c => c[1]);
            const bounds = [
                [Math.min(...lngs), Math.min(...lats)],
                [Math.max(...lngs), Math.max(...lats)],
            ];

            this.mapbox.map.fitBounds(bounds, {
                padding: { top: 50, bottom: 50, left: 50, right: 50 },
                maxZoom: 14,
                duration: 1500,
            }, { programmatic: true });
        },

        // ────────────────────────────────────────────────────
        // URL sync
        // ────────────────────────────────────────────────────
        loadFromUrl() {
            const params = new URLSearchParams(window.location.search);
            let mapConfig = null;

            const lat = parseFloat(params.get('lat'));
            const lng = parseFloat(params.get('lng'));
            const zoom = parseFloat(params.get('zoom'));

            if (!isNaN(lat) && !isNaN(lng)) {
                mapConfig = {
                    center: [lng, lat],
                    zoom: !isNaN(zoom) ? zoom : 13,
                };
            }

            // Load sort
            const sort = params.get('sort');
            if (sort && this.sortOptions.some(o => o.value === sort)) {
                this.sortBy = sort;
            }

            // Load filters
            const bedrooms = params.get('bedrooms');
            if (bedrooms) this.filters.bedrooms = bedrooms.split(',').map(Number);
            if (params.get('bedrooms_exact_match') === '1') this.filters.bedrooms_exact_match = true;

            const bathrooms = params.get('bathrooms');
            if (bathrooms) this.filters.bathrooms = bathrooms.split(',').map(Number);

            const minPrice = params.get('min_price');
            if (minPrice) this.filters.min_price = parseInt(minPrice);

            const maxPrice = params.get('max_price');
            if (maxPrice) this.filters.max_price = parseInt(maxPrice);

            if (params.get('has_parking') === '1') this.filters.has_parking = true;
            if (params.get('has_store') === '1') this.filters.has_store = true;

            const type = params.get('type');
            if (type && ['new_project', 'resale', 'all'].includes(type)) {
                this.filters.type = type === 'all' ? null : type;
            }

            // Sale-specific filters
            if (params.get('guaranteed_rent') === '1') this.filters.guaranteed_rent = true;
            if (params.get('installment_downpayment') === '1') this.filters.installment_downpayment = true;
            if (params.get('bono_pie') === '1') this.filters.bono_pie = true;
            if (params.get('immediate_delivery') === '1') this.filters.immediate_delivery = true;
            if (params.get('future_delivery') === '1') this.filters.future_delivery = true;

            // Load view mode
            if (params.get('mode') === 'map') this.viewMode = 'map';

            // Sync loaded filters to tempFilters
            this.syncFiltersToTemp();

            return mapConfig;
        },

        updateUrl(isMapEvent = false, isProgrammatic = false, forceCenter = null, forceZoom = null) {
            if (isMapEvent && isProgrammatic && Date.now() - this.lastProgrammaticMapMove < 3500) return;

            const params = new URLSearchParams();
            const map = this.mapbox?.map;

            if (map) {
                const center = forceCenter || map.getCenter();
                const zoom = forceZoom || map.getZoom();

                if (!this.communeSlug || this.viewMode === 'map') {
                    params.set('lat', center.lat.toFixed(6));
                    params.set('lng', center.lng.toFixed(6));
                    params.set('zoom', zoom.toFixed(2));
                }
            }

            if (this.viewMode === 'map') params.set('mode', 'map');

            // Filters
            if (this.filters.bedrooms?.length > 0) params.set('bedrooms', this.filters.bedrooms.join(','));
            if (this.filters.bedrooms_exact_match) params.set('bedrooms_exact_match', '1');
            if (this.filters.bathrooms?.length > 0) params.set('bathrooms', this.filters.bathrooms.join(','));
            if (this.filters.min_price) params.set('min_price', this.filters.min_price);
            if (this.filters.max_price) params.set('max_price', this.filters.max_price);
            if (this.filters.has_parking) params.set('has_parking', '1');
            if (this.filters.has_store) params.set('has_store', '1');
            if (this.filters.type) params.set('type', this.filters.type);
            // Sale-specific filters
            if (this.filters.guaranteed_rent) params.set('guaranteed_rent', '1');
            if (this.filters.installment_downpayment) params.set('installment_downpayment', '1');
            if (this.filters.bono_pie) params.set('bono_pie', '1');
            if (this.filters.immediate_delivery) params.set('immediate_delivery', '1');
            if (this.filters.future_delivery) params.set('future_delivery', '1');
            if (this.sortBy) params.set('sort', this.sortBy);

            if (this.communeName) params.set('q', this.communeName);

            // Build path
            let pathname = this.communeSlug
                ? `/ventas/resultados/${this.communeSlug}`
                : '/ventas/resultados';

            const qs = params.toString();
            const newUrl = qs ? `${pathname}?${qs}` : pathname;
            const currentUrl = window.location.pathname + window.location.search;

            if (newUrl !== currentUrl) {
                window.history.replaceState(null, '', newUrl);
            }
        },

        // ────────────────────────────────────────────────────
        // View mode
        // ────────────────────────────────────────────────────
        toggleViewMode() {
            this.viewMode = this.viewMode === 'list' ? 'map' : 'list';
            this.updateUrl();

            if (this.viewMode === 'map' && this.mapbox?.map) {
                // Need multiple resize attempts: first to recalculate the container,
                // then again after the DOM has fully transitioned
                setTimeout(() => this.mapbox.map.resize(), 50);
                setTimeout(() => {
                    this.mapbox.map.resize();
                    // Re-add markers and fit bounds if we have data
                    if (this.buildings.length > 0) {
                        this.lastProgrammaticMapMove = Date.now();
                        this.updateMapMarkers(this.buildings);
                        setTimeout(() => {
                            this.fitMapToBuildingBounds(this.buildings);
                        }, 100);
                    }
                }, 300);
            }
        },
    };
}
